import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Download, Loader2, Sparkles, Plus, RefreshCw,
  ClipboardList, Shield, BarChart3, Briefcase
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REPORT_TYPES = [
  { key: "underwriting_report", label: "Underwriting Report", icon: Shield, desc: "AI-powered risk analysis with strengths, risks, and recommendation" },
  { key: "borrower_summary", label: "Borrower Summary", icon: ClipboardList, desc: "Borrower profile, deal metrics, and document overview" },
  { key: "deal_summary", label: "Deal Summary", icon: Briefcase, desc: "Complete deal package with AI underwriting analysis" },
];

const REPORT_COLORS = {
  underwriting_report: "border-purple-200 bg-purple-50/30",
  borrower_summary: "border-blue-200 bg-blue-50/30",
  deal_summary: "border-emerald-200 bg-emerald-50/30",
};

export default function ReportCenterPage({ onViewDeal }) {
  const [reports, setReports] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [generating, setGenerating] = useState(null); // "underwriting_report:lead_id" format
  const [showGenerate, setShowGenerate] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("ic_token")}` };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/entity/reports/center`, { headers });
      setReports(res.data.reports || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchDeals = async () => {
    setDealsLoading(true);
    try {
      const res = await axios.get(`${API}/crm/dashboard-stats`, { headers });
      // Also get deals list from pipeline or another endpoint
      const pRes = await axios.get(`${API}/deals/active?limit=20`, { headers });
      setDeals(pRes.data || []);
    } catch (err) {
      // Fallback — try to get deals from search
      try {
        const sRes = await axios.get(`${API}/crm/search?q=&limit=20`, { headers });
        setDeals(sRes.data.results || []);
      } catch {
        setDeals([]);
      }
    }
    setDealsLoading(false);
  };

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerate = async (leadId, reportType) => {
    const key = `${reportType}:${leadId}`;
    setGenerating(key);
    try {
      const res = await axios.post(`${API}/reports/generate-and-save/${leadId}`, { report_type: reportType }, { headers });
      toast.success(`${res.data.filename} generated and saved`);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    }
    setGenerating(null);
  };

  const handleDownload = async (doc) => {
    try {
      if (doc.url) {
        window.open(doc.url, "_blank");
      } else if (doc.storage_path) {
        const res = await axios.get(`${API}/documents/${doc.doc_id}/download-url`, { headers });
        if (res.data.url) window.open(res.data.url, "_blank");
      }
    } catch {
      toast.error("Download failed");
    }
  };

  const allReports = Object.values(reports).flat();

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-[#888]">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading reports...
    </div>
  );

  return (
    <div className="space-y-5" data-testid="report-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#222]">Report Center</h2>
          <p className="text-xs text-[#888] mt-0.5">{total} report{total !== 1 ? "s" : ""} generated</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchReports()} className="p-2 rounded-lg hover:bg-[#f0ece2] text-[#888] transition-colors" data-testid="refresh-reports">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => { setShowGenerate(!showGenerate); if (!showGenerate && deals.length === 0) fetchDeals(); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#D4AF37] text-[#0A0A0A] text-xs font-semibold hover:bg-[#c9a030] transition-colors"
            data-testid="generate-report-btn">
            <Plus className="h-3.5 w-3.5" /> Generate Report
          </button>
        </div>
      </div>

      {/* Generate Report Panel */}
      {showGenerate && (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-[#faf7f2] p-5 space-y-4" data-testid="generate-report-panel">
          <div className="text-sm font-bold text-[#222]">Generate New Report</div>

          {dealsLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#888]"><Loader2 className="h-4 w-4 animate-spin" /> Loading deals...</div>
          ) : deals.length === 0 ? (
            <div className="text-sm text-[#888]">No active deals found.</div>
          ) : (
            <div className="space-y-3">
              {deals.slice(0, 10).map(deal => (
                <div key={deal.lead_id} className="rounded-lg border border-[#e8e0d0] bg-white p-3" data-testid={`gen-deal-${deal.lead_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs font-bold text-[#222]">{deal.deal_number || deal.lead_id?.slice(0,8)}</div>
                      <div className="text-[9px] text-[#888]">{deal.full_name || deal.borrower_name} — {deal.property_address || deal.state}</div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[9px] font-semibold bg-[#faf7f2] text-[#666] border border-[#e8e0d0]">{deal.stage}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {REPORT_TYPES.map(rt => {
                      const genKey = `${rt.key}:${deal.lead_id}`;
                      const isGen = generating === genKey;
                      return (
                        <button key={rt.key} onClick={() => handleGenerate(deal.lead_id, rt.key)} disabled={!!generating}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-semibold border transition-colors ${
                            REPORT_COLORS[rt.key] || "border-gray-200 bg-gray-50"
                          } hover:opacity-80 disabled:opacity-50`}
                          data-testid={`gen-${rt.key}-${deal.lead_id}`}>
                          {isGen ? <Loader2 className="h-3 w-3 animate-spin" /> : <rt.icon className="h-3 w-3" />}
                          {rt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report Type Sections */}
      {REPORT_TYPES.map(rt => {
        const typeReports = reports[rt.key] || [];
        if (typeReports.length === 0 && total === 0) return null;

        return (
          <div key={rt.key} className={`rounded-xl border p-4 ${REPORT_COLORS[rt.key] || "border-[#e8e0d0] bg-[#faf7f2]"}`} data-testid={`report-section-${rt.key}`}>
            <div className="flex items-center gap-2 mb-3">
              <rt.icon className="h-4 w-4 text-[#555]" />
              <div>
                <div className="text-sm font-bold text-[#222]">{rt.label}</div>
                <div className="text-[9px] text-[#888]">{rt.desc}</div>
              </div>
              <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold bg-white/50 text-[#555] border border-current/10">{typeReports.length}</span>
            </div>

            {typeReports.length === 0 ? (
              <div className="text-xs text-[#aaa] text-center py-4">No {rt.label.toLowerCase()} reports yet. Use "Generate Report" to create one.</div>
            ) : (
              <div className="space-y-2">
                {typeReports.map(rpt => (
                  <div key={rpt.doc_id} className="flex items-center justify-between rounded-lg bg-white/60 border border-white/80 p-3"
                    data-testid={`report-item-${rpt.doc_id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-[#D4AF37] shrink-0" />
                      <div>
                        <div className="text-xs font-medium text-[#222]">{rpt.original_filename || rpt.filename}</div>
                        <div className="text-[9px] text-[#888] flex items-center gap-2">
                          {rpt.deal_label && (
                            <button onClick={() => onViewDeal?.(rpt.lead_id)} className="text-[#D4AF37] hover:underline">
                              {rpt.deal_label}
                            </button>
                          )}
                          <span>{rpt.created_at ? new Date(rpt.created_at).toLocaleDateString() : ""}</span>
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            {rpt.visibility?.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleDownload(rpt)} title="Download"
                      className="p-1.5 rounded-lg hover:bg-[#f0ece2] text-[#888] hover:text-[#333] transition-colors"
                      data-testid={`report-download-${rpt.doc_id}`}>
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state if no reports at all */}
      {total === 0 && (
        <div className="text-center py-12 space-y-3" data-testid="reports-empty-state">
          <BarChart3 className="h-10 w-10 text-[#ddd] mx-auto" />
          <div className="text-sm font-bold text-[#222]">No Reports Generated Yet</div>
          <p className="text-xs text-[#888] max-w-md mx-auto">
            Click "Generate Report" above to create AI-powered underwriting reports, borrower summaries, or deal summaries for your active deals.
          </p>
        </div>
      )}
    </div>
  );
}
